<template>
  <div class="flex items-center gap-0.5 sm:gap-1 md:gap-2">
    <!-- Previous Song Button -->
    <div 
      class="p-0.5 sm:p-1 cursor-pointer transition-colors duration-200 group"
      @click="goToPrevious"
      :class="{ 'opacity-50 cursor-not-allowed': !canNavigate }"
    >
      <Tooltip :text="$t('tooltip.previousSong')" :position="'top'">
        <div class="flex items-center justify-center w-full h-full">
          <!-- Keyboard Key Background -->
          <div class="relative rounded-full bg-[#ffffff] dark:bg-gray-800 p-1.5 sm:p-2 md:p-3 shadow-md border border-gray-200 dark:border-gray-600 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-indigo-500 group-hover:to-purple-500 dark:group-hover:bg-gray-700 group-hover:border-transparent transition-colors cursor-pointer duration-150 flex items-center justify-center">
            <!-- Left Arrow Icon -->
            <ChevronLeftIcon class="md:w-5 md:h-5 w-4 h-4" />
          </div>
        </div>
      </Tooltip>
    </div>

    <!-- Next Song Button -->
    <div 
      class="p-0.5 sm:p-1 cursor-pointer transition-colors duration-200 group"
      @click="goToNext"
      :class="{ 'opacity-50 cursor-not-allowed': !canNavigate }"
    >
      <Tooltip :text="$t('tooltip.nextSong')" :position="'top'">
        <div class="flex items-center justify-center w-full h-full">
          <!-- Keyboard Key Background -->
          <div class="relative rounded-full bg-[#ffffff] dark:bg-gray-800 p-1.5 sm:p-2 md:p-3 shadow-md border border-gray-200 dark:border-gray-600 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-indigo-500 group-hover:to-purple-500 dark:group-hover:bg-gray-700 group-hover:border-transparent transition-colors cursor-pointer duration-150 flex items-center justify-center">
            <!-- Right Arrow Icon -->
            <ChevronRightIcon class="md:w-5 md:h-5 w-4 h-4" />
          </div>
        </div>
      </Tooltip>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import playlistStore from '@/store/playlist.store'
import appStore from '@/store/app.store'
import { useRoute, useRouter } from 'vue-router'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'

const route = useRoute()
const router = useRouter()

// Computed property to check if navigation is allowed
const canNavigate = computed(() => {
  return appStore.state.currentView === 'Playlist Songs' && 
         !appStore.state.disableEvents && 
         !playlistStore.state.loading
})

const getSongId = (song) => {
  return song?.data?._id || song?.data?.id || song?.song_id || song?.songID || song?.song || song?.id || song?._id
}

// Navigate to previous song
const goToPrevious = () => {
  if (!canNavigate.value) return
  
  playlistStore.dispatch("previous").then((currentSong) => {
    if (playlistStore.state.changedSong && currentSong) {
      const songId = getSongId(currentSong)
      if (songId) {
        router.push({
          name: "Playlist Songs", 
          params: { 
            id: route.params.id, 
            songId: songId
          } 
        })
      }
    }
  })
}

// Navigate to next song
const goToNext = () => {
  if (!canNavigate.value) return
  
  playlistStore.dispatch("next").then((currentSong) => {
    if (playlistStore.state.changedSong && currentSong) {
      const songId = getSongId(currentSong)
      if (songId) {
        router.push({
          name: "Playlist Songs", 
          params: { 
            id: route.params.id, 
            songId: songId 
          } 
        })
      }
    }
  })
}
</script>

<style scoped>
/* Additional styling for keyboard key appearance */
.group:hover .shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
